import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import {
  Container,
  Divider,
  Box,
  Grid,
  GridItem,
  Spinner,
} from '@chakra-ui/react';
import Breadcrumbs from '../../../common/Breadcrumbs';
import { MeContext } from '../../../context/MeContext';
import type { MeContextState } from '../../../store/types/account';
import PassesViewHeader from './PassesViewHeader';
import { useGetPassQuery } from '../../../store/redux/api/passApi';
import { useGetPassTemplateQuery } from '../../../store/redux/api/templateApi';
import PassBuilderPanel from './PassBuilderPanel';
import PreviewPanel from '../pass-templates-new/PreviewPanel';
import {
  buildPassPreviewData,
  type TemplateNfcSettings,
} from './utils/buildPassPreviewData';
import {
  buildFieldOverrides,
  buildLocalisedLabelOverrides,
} from './utils/passEditState';
import { getStoredFeaturedActions } from './utils/buildPassEnhancedPreview';
import { usePassEnhancedPreview } from './hooks/usePassEnhancedPreview';
import { truncate } from './utils/passHelpers';
import ActivitySection from './sections/ActivitySection';
import BuilderBanner from '../pass-templates-new/BuilderBanner';
import { useAppDispatch } from '../../../store/redux/hooks';
import { resetPass } from '../../../store/redux/slices/pass.slice';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';

const PassViewPageView = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isReadOnly = useIsReadOnly();
  const readOnlyInertProps = isReadOnly
    ? ({ inert: '' } as unknown as { inert?: boolean })
    : {};

  useEffect(() => {
    dispatch(resetPass());
  }, [location.key, dispatch]);

  const {
    data: passData,
    isLoading,
    isError,
  } = useGetPassQuery({ uuid: uuid ?? '' }, { skip: !uuid });
  const pass = passData?.data?.data;
  const template = passData?.meta?.template;

  const { data: templateDetail } = useGetPassTemplateQuery(
    { id: template?.uuid ?? '' },
    { skip: !template?.uuid }
  );
  const templateAttributes = templateDetail?.data?.attributes as
    | {
        nfc?: { enabled?: boolean; source?: 'uuid' | 'extId' | 'custom' };
        googleNfcEnabled?: { enabled?: boolean };
        images?: {
          logo?: string;
          icon?: string;
          banner?: string;
          apple?: {
            logo?: string;
            icon?: string;
            banner?: string;
            thumbnail?: string;
          };
          google?: { logo?: string; banner?: string; wideLogo?: string };
        };
      }
    | undefined;

  const templateNfc: TemplateNfcSettings | undefined = templateAttributes
    ? {
        enabled: templateAttributes.nfc?.enabled,
        source: templateAttributes.nfc?.source,
        googleNfc: templateAttributes.googleNfcEnabled?.enabled,
      }
    : undefined;

  const templateImages = templateAttributes?.images as
    | {
        logo?: string;
        icon?: string;
        banner?: string;
        apple?: {
          logo?: string;
          icon?: string;
          banner?: string;
          thumbnail?: string;
        };
        google?: { logo?: string; banner?: string; wideLogo?: string };
      }
    | undefined;

  const meCtx = useContext(MeContext) as {
    state: MeContextState & { localisationEnabled?: boolean };
  };
  const localisationEnabled = !!meCtx.state.localisationEnabled;

  const [selectedPassLanguage, setSelectedPassLanguage] = useState<
    string | null
  >(null);
  const passDefaultLanguage =
    ((templateAttributes as Record<string, unknown> | undefined)
      ?.defaultLanguage as string) || 'en';
  const passLanguages = useMemo(() => {
    if (!localisationEnabled) return [];
    const content = pass?.attributes?.passContent;
    if (!content) return [];
    const langs = new Set<string>();
    Object.values(content).forEach((field) => {
      if (field && typeof field === 'object' && 'localisation' in field) {
        const loc = (field as { localisation?: Record<string, unknown> })
          .localisation;
        if (loc) Object.keys(loc).forEach((l) => langs.add(l));
      }
    });
    return Array.from(langs);
  }, [pass]);

  const passIdentifier = truncate(pass?.attributes?.extId || pass?.id, 24);
  const breadcrumbLinks = [
    {
      linkName: 'Home',
      linkUrl: '/',
    },
    {
      linkName: 'passes',
      linkUrl: '/passes',
    },
    {
      linkName: passIdentifier,
    },
  ];

  const localisedPreviewData = useMemo(() => {
    if (!pass || !template) return undefined;
    const base = buildPassPreviewData(
      pass,
      template,
      templateImages,
      templateNfc
    );
    if (!selectedPassLanguage) return base;

    // Apply translation overrides from pass content
    const translatedValues: Record<string, string> = {};
    const content = pass.attributes.passContent;
    if (content) {
      Object.entries(content).forEach(([fieldId, field]) => {
        if (field && typeof field === 'object' && 'localisation' in field) {
          const loc = (
            field as { localisation?: Record<string, Record<string, string>> }
          ).localisation;
          const translated =
            loc?.[selectedPassLanguage]?.value ??
            (loc?.[selectedPassLanguage] as unknown);
          if (typeof translated === 'string' && translated) {
            translatedValues[fieldId] = translated;
          }
        }
      });
    }
    // Apply template default translations first (labels + default values)
    const templateFieldsRaw = (
      templateDetail?.data?.attributes as Record<string, unknown> | undefined
    )?.fields as
      | Record<
          string,
          Record<
            string,
            {
              localisation?: Record<
                string,
                { label?: string; defaultValue?: string }
              >;
            } | null
          >
        >
      | undefined;
    const labelOverrides = buildLocalisedLabelOverrides(
      templateFieldsRaw,
      selectedPassLanguage
    );

    // Then override with pass content translations (takes priority)
    const valueOverrides =
      Object.keys(translatedValues).length > 0
        ? buildFieldOverrides(template, translatedValues)
        : {};
    if (
      Object.keys(labelOverrides).length > 0 ||
      Object.keys(valueOverrides).length > 0
    ) {
      return { ...base, ...labelOverrides, ...valueOverrides };
    }
    return base;
  }, [
    pass,
    template,
    templateDetail,
    templateImages,
    templateNfc,
    selectedPassLanguage,
  ]);

  const previewData = usePassEnhancedPreview(
    templateAttributes,
    localisedPreviewData,
    getStoredFeaturedActions(pass)
  );

  const devModeData = useMemo(() => {
    if (!pass || !template) return undefined;
    const attrs = pass.attributes;
    const nfcRaw = attrs.nfc;
    const nfcEnabled =
      typeof nfcRaw === 'object' && nfcRaw !== null
        ? !!nfcRaw.enabled
        : !!nfcRaw;
    let nfcSource = 'uuid';
    let nfcCustomValue: string | null = null;
    if (typeof nfcRaw === 'string') {
      if (nfcRaw === attrs.extId) {
        nfcSource = 'extId';
      } else if (nfcRaw === pass.id) {
        nfcSource = 'uuid';
      } else {
        nfcSource = 'custom';
        nfcCustomValue = nfcRaw;
      }
    }

    const barcode = attrs.barcode;
    const barcodeEnabled = barcode?.enabled;

    const EXCLUDED_CONTENT_KEYS = [
      'barcode',
      'nfc',
      'expiresAt',
      'description',
      'centralTitle',
      'latestUpdate',
    ];
    const filteredContent: Record<string, unknown> = {};
    if (attrs.passContent) {
      Object.entries(attrs.passContent).forEach(([key, val]) => {
        if (!EXCLUDED_CONTENT_KEYS.includes(key)) {
          filteredContent[key] = val;
        }
      });
    }

    const passObj: Record<string, unknown> = {
      ...filteredContent,
      ...(barcodeEnabled ? { barcode } : { barcode: { enabled: false } }),
      ...(nfcEnabled
        ? {
            nfc: {
              enabled: true,
              source: nfcSource,
              ...(nfcSource === 'custom'
                ? { customValue: nfcCustomValue }
                : {}),
            },
          }
        : { nfc: { enabled: false } }),
      ...(attrs.expiresAt ? { expiresAt: attrs.expiresAt } : {}),
    };

    const data: Record<string, unknown> = { pass: passObj };
    if (attrs.groupTags?.length) {
      data.groupTags = attrs.groupTags;
    }
    return data;
  }, [pass, template]);

  if (isLoading) {
    return (
      <Spinner
        width="52px"
        height="52px"
        thickness="4px"
        speed="0.65s"
        emptyColor="quinaryBackground"
        m="auto"
      />
    );
  }

  if (isError || (!isLoading && !pass)) {
    return <Navigate to="/passes" replace />;
  }

  return (
    <Container display="flex" flexDir="column" flex="1" pb={6}>
      <BuilderBanner
        title="Welcome to the updated pass manager"
        subtitle="We've made improvements to help you issue and manage passes more easily."
        simpleMode
      />
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
      <PassesViewHeader />
      <Divider borderColor="#ADADAD" mt={4} mb={10} />
      <Grid templateColumns="minmax(0, 1fr) minmax(0, 1fr)" gap={10}>
        <GridItem minW={0}>
          <Box {...readOnlyInertProps} opacity={isReadOnly ? 0.6 : 1}>
            <PassBuilderPanel />
          </Box>
        </GridItem>
        <GridItem minW={0}>
          <Box display="flex" flexDirection="column" gap={8}>
            <PreviewPanel
              externalPreviewData={previewData}
              isPassPreview
              devModeData={devModeData}
              passLanguages={passLanguages}
              passDefaultLanguage={passDefaultLanguage}
              selectedPassLanguage={selectedPassLanguage}
              onPassLanguageChange={setSelectedPassLanguage}
              passDeviceType={pass?.attributes?.deviceType}
            />
            <ActivitySection attributes={pass?.attributes} />
          </Box>
        </GridItem>
      </Grid>
    </Container>
  );
};
export default PassViewPageView;
